/**
 * Pricing Table Component
 *
 * Responsive pricing table component that displays course pricing options.
 * Shows as a traditional table on desktop and stacks as cards on mobile.
 */

import React from 'react';
import { CoursePrice } from '../../types/course';

interface PricingTableProps {
  pricing: CoursePrice[];
  className?: string;
}

const PricingTable: React.FC<PricingTableProps> = ({
  pricing,
  className = '',
}) => {
  // Format price
  const formatPrice = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get pricing type display name
  const getPricingTypeDisplay = (type: string): string => {
    switch (type) {
      case 'individual': return 'Individual';
      case 'early-bird': return 'Early Bird';
      case 'group': return 'Group Rate';
      default: return type;
    }
  };

  // Get pricing type badge color
  const getPricingTypeBadge = (type: string): string => {
    switch (type) {
      case 'individual': return 'bg-indigo_dye-100 text-indigo_dye-800';
      case 'early-bird': return 'bg-navajo_white-400 text-navajo_white-900';
      case 'group': return 'bg-amaranth_purple-100 text-amaranth_purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (pricing.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No pricing information available.
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View - Hidden on mobile */}
      <div className={`hidden md:block overflow-x-auto ${className}`}>
        <table className="w-full border-collapse bg-white border-4 border-gray-400 overflow-hidden">
          <thead>
            <tr className="bg-indigo_dye-500 text-white border-b-4 border-gray-400">
              <th className="px-6 py-4 text-left font-mono text-sm font-bold uppercase tracking-wide">
                Pricing Type
              </th>
              <th className="px-6 py-4 text-left font-mono text-sm font-bold uppercase tracking-wide">
                Price
              </th>
              <th className="px-6 py-4 text-left font-mono text-sm font-bold uppercase tracking-wide">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {pricing.map((price, index) => (
              <tr
                key={index}
                className={`border-b-2 border-gray-300 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } hover:bg-amaranth_purple-50 transition-colors`}
              >
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 border-2 font-mono text-sm font-bold uppercase ${getPricingTypeBadge(price.type)}`}>
                    {getPricingTypeDisplay(price.type)}
                  </span>
                </td>
                <td className="px-6 py-4 font-display text-xl font-bold text-amaranth_purple-600">
                  {formatPrice(price.amount)}
                </td>
                <td className="px-6 py-4 font-body text-sm text-gray-700">
                  {price.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Hidden on desktop */}
      <div className={`md:hidden space-y-4 ${className}`}>
        {pricing.map((price, index) => (
          <div
            key={index}
            className="bg-white border-4 border-gray-400 p-4 shadow-[4px_4px_0px_0px_rgba(2,5,18,0.5)]"
          >
            <div className="flex items-start justify-between mb-3">
              <span className={`px-3 py-1 border-2 font-mono text-sm font-bold uppercase ${getPricingTypeBadge(price.type)}`}>
                {getPricingTypeDisplay(price.type)}
              </span>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-amaranth_purple-600">
                  {formatPrice(price.amount)}
                </div>
              </div>
            </div>
            <p className="font-body text-sm text-gray-700">
              {price.details}
            </p>
          </div>
        ))}
      </div>
    </>
  );
};

export default PricingTable;
